import css from './TraineeshipTransition.module.scss';
import classnames from 'classnames';
import React, { FC, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Box, Tag } from '@core';
import { PageLoaderContext, ViewportContext } from '@context';
import { useScrollWithEase } from '@hooks';
import { normalize } from '@utils';
import useIntersectionObserver from '@hooks/useIntersectionObserver';

export interface TraineeshipTransitionProps {
	title?: string;
	picture?: Picture;
}

export interface TraineeshipTransitionMaskProps {
	scale: number;
	opacity: number;
	title?: string;
}

const steps = 4;
const caseDiff = 0.153846153846154;

export const TraineeshipTransition: FC<TraineeshipTransitionProps> = React.memo(
	({ title, picture }) => {
		const ref = useRef<HTMLDivElement | null>(null);

		const { ready } = useContext(PageLoaderContext);
		const { vh, bp } = useContext(ViewportContext);
		const [seek, setSeek] = useState(0);

		const onScroll = useCallback(
			(current: number, height: number) => {
				const start = height - vh;
				const end = current - vh;
				const absolute = 1 - end / start;
				const relative = Math.max(Math.min(absolute, 1), 0);

				setSeek(relative);
			},
			[vh]
		);

		useScrollWithEase(ref, onScroll, {
			ease: 0.35,
			edge: 'bottom',
		});

		const isMob = ['xs', 'sm'].includes(bp || 'hd');

		const scaleS = (isMob ? 360 : 200) * (1 - normalize(seek, 0.6, 0, true));
		const scaleE = 30 * (1 - normalize(seek, 0.9, 0.6, true)) + 1;
		const scale = scaleS + scaleE;

		// const scale =
		// 	(isMob ? 300 : 130) * (1 - Math.pow(normalize(seek, isMob ? 0.8 : 0.9, 0, true), 1.5)) + 1;

		const opacity = normalize(seek, 0.9, 0.8, true);

		return (
			<Box
				ref={ref}
				className={css.section}
				style={{
					height: `${100 * (isMob ? steps * 0.5 : steps)}vh`,
				}}>
				<Box className={css.track}>
					<Box className={css.sticky}>
						<Box className={css.stickyInner}>
							{picture && (
								<Image
									src={picture.src}
									alt={picture?.alt || title}
									layout="fill"
									quality={100}
									priority={true}
								/>
							)}
						</Box>
					</Box>
				</Box>
				<Box className={classnames(css.mask, { [css.isInteractive]: opacity === 1 })}>
					{/*
					 * Это вариант с маской в виде простого path
					 */}
					<TraineeshipTransitionMask title={title} scale={ready ? scale : 1} opacity={opacity} />
					{/*
					 * Это вариант с текстовой маской (есть баг в safari: растрирует маску при скейле, поэтому захардкодили текст в виде простого path)
					 */}
					{/*<TraineeshipTransitionTextMask title={title} scale={ready ? scale : 1} opacity={opacity} />*/}
				</Box>
			</Box>
		);
	}
);

export const TraineeshipTransitionMaskSvgText: FC = React.memo(() => {
	const { locale } = useRouter();
	return useMemo(() => {
		switch (locale) {
			case 'en':
				return (
					<svg viewBox="0 0 536 100">
						<path
							d="M-1-1h538v102H-1V-1zm18.296 18.3H7.912V84h9.384V17.3zm21.208 17.204h-8.372V84h8.372V57.136c0-10.672 4.968-16.192 13.064-16.192 9.568 0 10.856 6.624 10.856 14.26V84h8.372V52.72c0-11.316-4.324-19.044-16.284-19.044-8.372 0-13.156 3.588-16.008 8.28v-7.452zm45.496 0h-7.82v6.716H84v30.36C84 81.884 86.208 84 95.684 84h6.072v-6.992h-4.232c-4.784 0-5.152-1.288-5.152-6.44V41.22h9.384v-6.716h-9.384v-12.42H84v12.42zm69.048 27.508c1.012-15.732-7.912-28.336-23.092-28.336-13.524 0-24.012 9.936-24.012 25.668 0 14.628 8.648 25.852 24.288 25.852 12.052 0 19.964-7.084 22.172-15.548h-8.648c-2.116 4.876-6.164 8.188-13.524 8.188-7.268 0-14.996-5.152-15.272-15.824h38.088zm-23.092-21.16c8.924 0 13.984 6.992 14.26 13.984h-29.164c.736-9.936 7.544-13.984 14.904-13.984zm55.8-6.532c-1.012-.092-1.656-.092-2.484-.092-6.532.276-11.5 3.312-13.984 8.924v-8.648H161.1V84h8.372V58.056c0-10.948 3.864-15.18 11.592-15.18a29.9 29.9 0 014.692.368V34.32zm14.676.184h-8.372V84h8.372V57.136c0-10.672 4.968-16.192 13.064-16.192 9.568 0 10.856 6.624 10.856 14.26V84h8.372V52.72c0-11.316-4.324-19.044-16.284-19.044-8.372 0-13.156 3.588-16.008 8.28v-7.452zm80.18 14.444c-1.564-9.476-8.74-15.364-19.504-15.364-10.948 0-19.78 5.06-19.78 13.8 0 9.292 7.36 12.236 14.076 13.708l7.36 1.472c5.336 1.196 10.028 3.128 10.028 8.188 0 4.692-5.152 7.36-11.5 7.36-7.36 0-11.224-3.772-12.604-10.12h-8.832c1.012 8.648 8.28 17.204 21.252 17.204 11.316 0 20.148-5.52 20.148-15.364 0-9.476-7.912-13.064-16.928-14.72l-6.992-1.38c-4.784-1.012-7.544-2.76-7.544-6.716 0-4.508 5.52-6.256 10.396-6.256 5.888 0 10.304 2.3 11.684 8.188h8.74zM298.508 17.3h-8.372V84h8.372V57.136c0-10.672 4.968-16.192 13.064-16.192 9.568 0 10.856 6.624 10.856 14.26V84h8.372V52.72c0-11.316-4.324-19.044-16.284-19.044-8.372 0-13.156 3.588-16.008 8.28V17.3zm43.104 17.204V84h8.372V34.504h-8.372zm0-17.204v10.12h8.372V17.3h-8.372zm19.92 17.204V100.1h8.372V76.548c3.036 5.244 8.188 8.648 16.192 8.648 11.408 0 21.16-9.292 21.16-25.76 0-16.192-9.752-25.76-21.16-25.76-8.004 0-13.156 3.404-16.192 8.648v-7.82h-8.372zm8.004 24.932c0-13.064 6.992-18.584 14.444-18.584 8.556 0 14.168 6.716 14.168 18.584 0 11.868-5.612 18.584-14.168 18.584-7.452 0-14.444-5.52-14.444-18.584z"
							fillRule="nonzero"
						/>
					</svg>
				);
			default:
				return (
					<svg viewBox="0 0 536 100">
						<path d="M0 0h536v100H0V0zm55.048 62.644C52.932 71.2 46.492 77.18 35.82 77.18c-12.42 0-21.252-9.844-21.252-26.772 0-17.296 9.2-26.496 20.976-26.496 10.58 0 17.204 6.164 19.136 14.536h9.108C60.844 24.188 49.804 16 36.004 16 17.236 16 5 29.8 5 50.408c0 20.24 11.132 34.684 30.268 34.684 14.444 0 25.944-7.728 28.888-22.448h-9.108zm53.782-20.976V34.4H66.602v7.268H83.53v42.228h8.372V41.668h16.928zm34.77 21.528c0 11.684-7.452 15.272-15.272 15.272-5.98 0-9.568-3.128-9.568-7.82 0-6.808 9.66-9.108 24.84-10.58v3.128zm-17.112 21.896c8.832 0 13.892-2.852 16.928-8.004.092 2.944.368 4.968.644 6.808h8.464c-1.012-4.692-1.38-10.304-1.288-18.676l.092-11.408c.092-13.984-5.98-20.424-19.688-20.424-9.568 0-19.136 5.98-19.872 16.652h8.648c.368-6.164 4.324-9.844 11.316-9.844 6.256 0 11.224 2.944 11.224 12.052v1.196c-20.24 2.208-33.488 5.704-33.488 17.388 0 9.108 7.728 14.26 17.02 14.26zm84.419-27.324L227.375 34.4h-9.66l-13.892 20.332h-6.992V34.4h-8.372v20.332h-6.992l-13.8-20.332h-9.752l16.56 23.644-18.216 25.852h10.212L181.467 62h6.992v21.896h8.372V62h7.268l14.904 21.896h10.028l-18.124-26.128zm58.194 26.128h8.372V34.4h-8.372l-25.576 37.352V34.4h-8.372v49.496h8.372l25.576-37.352v37.352zM289.825 34.4v65.596h8.372V76.444c3.036 5.244 8.188 8.648 16.192 8.648 11.408 0 21.16-9.292 21.16-25.76 0-16.192-9.752-25.76-21.16-25.76-8.004 0-13.156 3.404-16.192 8.648V34.4h-8.372zm8.004 24.932c0-13.064 6.992-18.584 14.444-18.584 8.556 0 14.168 6.716 14.168 18.584 0 11.868-5.612 18.584-14.168 18.584-7.452 0-14.444-5.52-14.444-18.584zm52.181 0c0-13.524 7.176-18.4 14.812-18.4 7.636 0 14.812 5.152 14.812 18.4 0 12.696-7.176 18.4-14.812 18.4-7.636 0-14.812-5.152-14.812-18.4zm14.812-25.76c-11.5 0-23.736 7.82-23.736 25.76s12.236 25.76 23.736 25.76 23.736-7.82 23.736-25.76-12.236-25.76-23.736-25.76zm32.37.828v49.496h23c10.58 0 18.584-4.14 18.584-14.444 0-6.624-3.772-10.12-8.924-11.592 4.6-1.932 6.9-5.612 6.9-10.856 0-10.028-8.096-12.604-18.032-12.604h-21.528zm8.372 20.792V41.668h12.328c6.256 0 10.12 1.104 10.12 6.256 0 5.612-3.68 7.268-9.844 7.268h-12.604zm0 6.9h13.064c7.728 0 11.408 1.748 11.408 7.268 0 5.152-3.772 7.268-11.132 7.268h-13.34V62.092zm73.01 21.804h10.396l-20.608-26.128L487.038 34.4h-9.936L461.37 54.64h-6.164V34.4h-8.464v49.496h8.464V61.908h6.348l17.02 21.988zm46.111-20.7c0 11.684-7.452 15.272-15.272 15.272-5.98 0-9.568-3.128-9.568-7.82 0-6.808 9.66-9.108 24.84-10.58v3.128zm-17.112 21.896c8.832 0 13.892-2.852 16.928-8.004.092 2.944.368 4.968.644 6.808h8.464c-1.012-4.692-1.38-10.304-1.288-18.676l.092-11.408c.092-13.984-5.98-20.424-19.688-20.424-9.568 0-19.136 5.98-19.872 16.652h8.648c.368-6.164 4.324-9.844 11.316-9.844 6.256 0 11.224 2.944 11.224 12.052v1.196c-20.24 2.208-33.488 5.704-33.488 17.388 0 9.108 7.728 14.26 17.02 14.26z" />
					</svg>
				);
		}
	}, [locale]);
});

const TraineeshipTransitionMask: FC<TraineeshipTransitionMaskProps> = React.memo(
	({ title, scale, opacity }) => {
		const { locale } = useRouter();
		const anchorIndex = useMemo(() => (locale === 'en' ? 7 : 4), [locale]);

		const { vh, vw } = useContext(ViewportContext);
		const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
		const [scaleOrigin, setScaleOrigin] = useState('50% 50%');

		const computeScaleOrigin = useCallback(() => {
			if (anchor) {
				const letter = anchor.querySelector<HTMLSpanElement>(`.anchor-letter`);

				if (letter) {
					const rect = letter.getBoundingClientRect();
					const anchorRect = anchor.getBoundingClientRect();

					const x = Math.floor(rect.left + rect.width * 0.5);
					const y = rect.top - anchorRect.top + rect.height * (0.5 + caseDiff / 1.8);

					const xRatio = Math.max(0, Math.min(1, x / vw)) * 100;
					const yRatio = Math.max(0, Math.min(1, y / vh)) * 100;

					setScaleOrigin(`${xRatio.toFixed(2)}% ${yRatio.toFixed(2)}%`);
				}
			}
		}, [anchor, vh, vw]);

		const entry = useIntersectionObserver(anchor, {
			freezeOnceVisible: true,
		});

		useEffect(() => {
			computeScaleOrigin();
		}, [computeScaleOrigin, entry]);

		return (
			<>
				<Box
					className={classnames(css.maskStatic, 'h1')}
					style={{
						transform: `scale(${scale})`,
						transformOrigin: scaleOrigin,
					}}>
					<div className={css.maskStaticColumn}>
						<div className={css.maskStaticPad} />
						<div className={css.maskStaticCenter}>
							<div className={css.maskStaticWords}>
								<div
									className={css.maskStaticWordsBase}
									style={{
										opacity,
									}}
								/>
								<TraineeshipTransitionMaskSvgText />
								<Tag className={css.maskStaticWordsText} type="h1" isDiv={true} aria-hidden={true}>
									{title}
								</Tag>
							</div>
						</div>
						<div className={css.maskStaticPad} />
					</div>
				</Box>
				<Box ref={setAnchor} onLoadCapture={computeScaleOrigin} className={css.maskScaleAnchor}>
					<Tag
						style={{
							opacity,
						}}
						className={css.maskStaticWordsText}
						type="h1"
						isDiv={true}>
						{title &&
							title.split('').map((letter, i) => {
								return (
									<span
										key={`mask-title-letter-${i}-${letter}`}
										className={classnames(anchorIndex === i + 1 && 'anchor-letter')}>
										{letter}
									</span>
								);
							})}
					</Tag>
				</Box>
			</>
		);
	}
);

// const TraineeshipTransitionTextMask: FC<TraineeshipTransitionMaskProps> = React.memo(
// 	({ title, scale, opacity }) => {
// 		const { bp } = useContext(ViewportContext);
//
// 		const indents: Record<Breakpoint, string> = {
// 			xs: '16px',
// 			sm: '32px',
// 			md: '10%',
// 			lg: '20%',
// 			hd: '20%',
// 		};
//
// 		return (
// 			<Box
// 				className={classnames(css.maskEditable, 'h1')}
// 				style={{
// 					transform: `scale(${scale})`,
// 				}}>
// 				<svg className={css.maskEditableVector}>
// 					<defs>
// 						<mask id="knockout" x="0" y="0" width="100%" height="100%">
// 							<rect x="0" y="0" width="100%" height="100%" fill="#fff" />
// 							<text
// 								x={bp ? indents[bp] : '20%'}
// 								y="50%"
// 								dominantBaseline="central"
// 								className={css.maskEditableText}
// 								fill="#000"
// 								style={{
// 									opacity: 1 - opacity,
// 								}}>
// 								{title}
// 							</text>
// 						</mask>
// 					</defs>
// 					<rect
// 						x="0"
// 						y="0"
// 						width="100%"
// 						height="100%"
// 						className={css.maskEditableBase}
// 						style={{
// 							mask: 'url(#knockout)',
// 						}}
// 					/>
// 					<text
// 						x={bp ? indents[bp] : '20%'}
// 						y="50%"
// 						dominantBaseline="central"
// 						className={classnames(css.maskReadableText, css.maskEditableText)}
// 						style={{
// 							opacity: Math.max(opacity, 0.001),
// 						}}>
// 						{title}
// 					</text>
// 				</svg>
// 			</Box>
// 		);
// 	}
// );
